// Emoticons (#630): when a message is sent, a standalone ":(" becomes 🙁. The box shows what was typed until then. One
// table and one rule for every client; the cases are shared/cases/emoticons.json, which the Swift and Kotlin ports run too.
import { splitCode } from "./markdown.ts";

/** The emoticons and the emoji they become. Exactly these; the match is case-sensitive. */
export const EMOTICONS: Readonly<Record<string, string>> = {
  ":)": "🙂",
  ":-)": "🙂",
  ":(": "🙁",
  ":-(": "🙁",
  ":D": "😄",
  ":-D": "😄",
  ";)": "😉",
  ";-)": "😉",
  ":P": "😛",
  ":-P": "😛",
  ":p": "😛",
  ":O": "😮",
  ":o": "😮",
  ":'(": "😢",
  "<3": "❤️",
};

/** What separates words here: a space, a tab, a line break or a no-break space (the same set on every client). */
const SPACE = new Set([" ", "\t", "\n", "\r", " "]);

/**
 * The text as it is sent: each standalone emoticon becomes its emoji. Standalone means the whole word: it starts the text
 * or follows a space, and it ends the text or comes before a space. So "a:(b", "(see :P)", "http://x" and "<3!" stay as
 * typed, and so does anything inside `code` or a code block. A link, an @mention and a #channel are words of their own,
 * which are never an emoticon. Only the emoticon changes: every other character stays, so no width moves.
 */
export function applyEmoticons(text: string): string {
  if (!text) return text;
  // Where the code is: offsets [from, to) of each code part. A word that starts inside one is code.
  const code: [number, number][] = [];
  if (text.includes("`")) {
    let at = 0;
    for (const part of splitCode(text)) {
      if (part.code) code.push([at, at + part.s.length]);
      at += part.s.length;
    }
  }
  const inCode = (i: number) => code.some(([from, to]) => i >= from && i < to);
  let out = "";
  let i = 0;
  while (i < text.length) {
    if (SPACE.has(text[i]!)) {
      out += text[i++];
      continue;
    }
    let j = i;
    while (j < text.length && !SPACE.has(text[j]!)) j++;
    const word = text.slice(i, j);
    out += Object.hasOwn(EMOTICONS, word) && !inCode(i) ? EMOTICONS[word]! : word;
    i = j;
  }
  return out;
}
